import { r, s } from '@/theme/tokens';
import { Box, ChevronRightIcon, ExpandIcon, Text } from '@/ui/primitives';
import type { ReactElement } from 'react';

type FlowStepsSectionProps = {
  width: number;
};

const underlineColors = ['primary', 'purple', 'pink'] as const;

const steps = [
  {
    title: 'Run Your Tests',
    bullets: ['Manually, automated, or CI', 'All you need is a structured results file'],
  },
  {
    title: 'Upload the Results',
    bullets: [
      'Results are processed automatically',
      'Suites and tests grouped and aggregated',
      'No manual intervention required',
    ],
  },
  {
    title: 'Analyse Quality',
    bullets: [
      'Understand quality clearly over time',
      'Less time spent investigating results',
      'Clear next steps, grounded in data',
    ],
  },
] as const;

export const FlowStepsSection = ({ width }: FlowStepsSectionProps): ReactElement => {
  const gridItems = steps.flatMap((step, index) => {
    const items = [
      <Box
        key={step.title}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: r('card'),
          paddingY: s('2xl'),
          paddingX: s('xl'),
          paddingTop: s('xl'),
          backgroundColor: 'background.paper',
          height: '100%',
        }}
      >
        <Text role="sectionTitle" component="h3" transform="none" mb="sm">
          <Box
            component="span"
            sx={(theme) => ({
              display: 'inline-flex',
              paddingBottom: s('md'),
              borderBottom: `${s('xs')} solid ${
                theme.app.colors[underlineColors[index % underlineColors.length]]
              }`,
            })}
          >
            {step.title}
          </Box>
        </Text>
        <Box component="ul" sx={{ m: s('none'), pl: s('lg'), color: 'text.secondary' }}>
          {step.bullets.map((bullet) => (
            <li key={bullet}>
              <Text role="bodyMuted" component="span">
                {bullet}
              </Text>
            </li>
          ))}
        </Box>
      </Box>,
    ];

    if (index < steps.length - 1) {
      items.push(
        <Box
          key={`${step.title}-chevron`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            px: s('sm'),
            py: { xs: s('sm'), md: s('none') },
          }}
        >
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <ExpandIcon sx={{ fontSize: 36 }} />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <ChevronRightIcon sx={{ fontSize: 36 }} />
          </Box>
        </Box>,
      );
    }

    return items;
  });

  return (
    <Box component="section" sx={{ px: { xs: s('3xl'), sm: s('4xl'), md: s('none') } }}>
      <Box sx={{ maxWidth: width, mx: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr)',
            },
            rowGap: s('lg'),
            columnGap: s('lg'),
            alignItems: 'stretch',
          }}
        >
          {gridItems}
        </Box>
      </Box>
    </Box>
  );
};
